import { BoxContainer, PrimaryButton, ReactIcons, Switch, useThemeContext } from "@kalyzee/kalyzee-ui"

import { Touchable, UserAvatar, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { getNormalUser } from "../../helpers/user";
import { isExpert } from "../../helpers/roles";
import { RoleBadge } from "../common/RoleBadge";
import { useAuthContext } from "@kalyzee/kast-web-sdk";

import styles from "./SettingsWorkBox.module.css";
import workBoxStyles from "./WorkBox.module.css";
import { APP_VERSION } from "../../helpers/utils";
import { usePageContextRef } from "../navigation/PageContext";
import { SessionMode } from "../../contexts/appStore/hooks";
import React, { useRef } from "react";
import { useSettings } from "../../hooks/settings";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";

export interface SettingsWorkBoxProps {
  style?: React.CSSProperties;
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

const _SettingsWorkBox = ({
  style = {},
  className,
  containerRef,
  onClose,
}: SettingsWorkBoxProps) => {
  const user = useAppStoreSelector(state => state.user);
  const authCtx = useAuthContext();
  const themeCtx = useThemeContext();

  const versionRef = useRef<HTMLDivElement>(null);
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();

  const [settings] = useSettings();

  const containerCtx = useMeetingContainerContext({ raiseIfUndefined: false });
  const [session] = useMeetingSelector(containerCtx?.containerAPI, "session");

  const { displayName, avatar } = user ? getNormalUser(user) : { avatar: undefined, displayName: undefined };

  // press 'k' and click 3 times with mouse(left button) on logo
  useTriggerable(versionRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup());

  return (
    <BoxContainer
      containerRef={containerRef}
      style={style}
      className={className}
      topBarProps={{
        left: [
          <ReactIcons.Settings style={{ width: 31 }} />,
        ],
        center: [
          <span className={workBoxStyles.label}>Paramètres</span>,
        ],
        right: onClose ? [
          <PrimaryButton onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>
        ] : [],
      }}
    >
      <div className={styles.container}>
        {user && (
          <>
            <div className={styles.userData}>
              <UserAvatar 
                className={styles.userIcon} 
                mode="image"
                image={avatar}  
                username={displayName}
              />
              <div className={styles.userInfo}>
                <div className={styles.userName}>{displayName}</div>
                {user.identity?.email && <div className={styles.userEmail}>{user.identity.email}</div>}
                {session && (isSpectator(session) || isExpert(session)) && (
                  <div className={styles.badgeRow}>
                    {isSpectator(session) && <RoleBadge variant="spectator" />}
                    {isExpert(session) && <RoleBadge variant="expert" />}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.separator} />
          </>
        )}
        <div className={styles.theme}>
          Dark mode
          <Switch 
            className={styles.themeSwitch} 
            value={themeCtx.theme === "dark"}
            onChange={() => themeCtx.setPreferredTheme(themeCtx.theme === "dark" ? "light" : "dark")}
          />
        </div>
        <div className={styles.footer}>
          {settings?.ui?.hideLogoutButton || <Touchable 
            className={styles.logoutBtn}
            onClick={() => { sessionStorage.setItem('explicit_logout', '1'); authCtx.logout(); }}
          >
            Se déconnecter
          </Touchable>}
          <div ref={versionRef} className={styles.version}>Version {APP_VERSION ?? "dev"}</div>
        </div>
      </div>
    </BoxContainer>
  );
}

export const SettingsWorkBox = React.memo(_SettingsWorkBox);